import { useArrMrr } from "@/services/context/ArrMrrContext";
import Text from "@/components/ui/text";
import { formatNumber } from "@/utils/numbers";
import { cn } from "@/utils/utils";
import { map } from "lodash";
import { ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton } from "@/components/ui/icon-button";
import { useSidebarState } from "@/services/context/SidebarContext";
import { deltaSign } from "@/entities/dataTables/compareTo/columnIds";

export interface KPI<T extends Record<string, number>> {
  name: keyof T;
  isMonetary?: boolean;
  unit?: string;
  /**
   * Overrides the `${pageName}.bottomKPI.<name>` translation, for a tile whose
   * label is composed rather than looked up — the Compare-to tile names the
   * selected timeframe, which no static key can carry.
   */
  label?: string;
  /**
   * Renders the figure as a movement: an explicit `+`/`-` ahead of the
   * magnitude, green for a rise and red for a fall, matching the Δ columns.
   */
  isDelta?: boolean;
  // Which request feeds this tile. The two now fail independently, so each
  // tile follows its own source rather than a single bar-wide error.
  isRowsDerived?: boolean;
  // A tile combining both requests - an aggregations numerator over a
  // rows-derived denominator - has no value to show unless both succeeded.
  needsBothSources?: boolean;
}

/**
 * Green up, red down — the same tokens the Δ cells use. Keyed on whether a sign
 * was rendered, so a movement that rounded away stays uncoloured as well as
 * unsigned rather than being tinted for a direction the figure does not show.
 */
const deltaColor = (value: number, isSigned: boolean): string | undefined => {
  if (!isSigned) return undefined;
  return value > 0 ? "var(--charts-positive)" : "var(--charts-negative)";
};

interface Props<T extends Record<string, number>> {
  kpis: KPI<T>[];
  data: T;
  pageName: string;
  isLoading?: boolean;
  isError?: boolean;
  isRowsError?: boolean;
  isHidden?: boolean;
  onOpenStateChange?: (isOpen: boolean) => void;
}

const BottomKPIBar = <T extends Record<string, number>>({
  kpis,
  data,
  pageName,
  isLoading = false,
  isError = false,
  isRowsError = false,
  isHidden = false,
  onOpenStateChange
}: Props<T>) => {
  const { t } = useTranslation();
  const { label: arrMrrLabel } = useArrMrr();
  const { isSidebarOpen } = useSidebarState();

  const [isOpen, setIsOpen] = useState(!isLoading);
  const [hasUserToggled, setHasUserToggled] = useState(false);

  useEffect(() => {
    setIsOpen(!isLoading);
  }, [isLoading]);

  useEffect(() => {
    onOpenStateChange?.(isOpen);
  }, [isOpen, onOpenStateChange]);

  const renderNumber = (kpi: KPI<T>) => {
    // Rendering 0 for a number that failed to load would present the failure
    // as a real figure, so show that there is no value instead. Each tile
    // follows the request that feeds it: a tile whose own source succeeded
    // keeps its number, because blanking that would hide a figure the bar
    // knows and has right.
    const hasFailed = kpi.needsBothSources
      ? isRowsError || isError
      : kpi.isRowsDerived
        ? isRowsError
        : isError;
    if (hasFailed) {
      return (
        <Text
          data-test={`bottom-kpi-${String(kpi.name)}-value`}
          variant="label"
        >
          —
        </Text>
      );
    }

    const raw = data[kpi.name] || 0;
    // A delta formats its magnitude and carries the direction in the sign, so
    // Intl never gets the chance to emit a minus of its own — which would land
    // after the currency symbol ("$-4,000") rather than ahead of it.
    const { value, currencySign } = formatNumber(
      kpi.isDelta ? Math.abs(raw) : raw,
      0,
      kpi.isMonetary,
      false
    );
    // `deltaSign` is shared with the Δ column cells, so a tile and the column
    // describing the same metric over the same period cannot disagree about
    // whether it moved — including the rounds-to-zero case that would otherwise
    // render "+$0".
    // `0` because these are whole-currency tiles — the same precision handed
    // to `formatNumber` above. See `deltaSign` on why it owns the rounding.
    const sign = kpi.isDelta ? deltaSign(raw, 0) : "";
    const color = deltaColor(raw, sign !== "");
    const style = color ? { color } : undefined;
    return (
      <>
        {sign ? (
          <Text
            data-test={`bottom-kpi-${String(kpi.name)}-sign`}
            variant="label"
            style={style}
          >
            {sign}
          </Text>
        ) : null}
        {kpi?.isMonetary && (
          <Text
            data-test={`bottom-kpi-${String(kpi.name)}-currency`}
            variant="label"
            className="pb-[1px]"
            style={style}
          >
            {currencySign}
          </Text>
        )}
        <Text
          data-test={`bottom-kpi-${String(kpi.name)}-value`}
          variant="label"
          style={style}
        >
          {value}
        </Text>
      </>
    );
  };

  return (
    <div
      className={cn(
        "fixed right-5 z-50 bottom-[var(--bottom-kpi-bar-space)] pointer-events-none transition-opacity duration-300",
        isOpen && (isSidebarOpen ? "left-[227px]" : "left-[75px]"),
        isHidden && "opacity-0"
      )}
    >
      <div
        className={cn(
          "flex items-center relative bg-surface-floating-panel rounded-2xl border-[0.5px] border-border-border2 shadow-popUp pointer-events-auto",
          hasUserToggled && "transition-all duration-300 ease-in-out",
          isOpen
            ? "justify-between min-h-12 px-6 py-2"
            : "w-12 h-12 justify-center"
        )}
      >
        {isOpen && (
          <div className="flex items-center gap-10">
            {map(kpis, (kpi) => (
              <div key={String(kpi.name)} className="flex items-baseline gap-2">
                <div className="flex items-baseline gap-[1px]">
                  {renderNumber(kpi)}
                  {kpi?.unit && (
                    <Text variant="label" className="pl-1">
                      {kpi?.unit}
                    </Text>
                  )}
                </div>
                <Text
                  data-test={`bottom-kpi-${String(kpi.name)}-name`}
                  variant="smBodySecondary"
                >
                  {kpi.label ??
                    t(`${pageName}.bottomKPI.${String(kpi.name)}`, {
                      arrMrr: arrMrrLabel
                    })}
                </Text>
              </div>
            ))}
          </div>
        )}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center">
          <IconButton
            className={cn("rounded-t-lg", isOpen && "border-transparent")}
            onClick={() => {
              setHasUserToggled(true);
              setIsOpen((prev) => !prev);
            }}
            size="lg"
            variant="iconNoBg"
            iconClassName={cn(
              hasUserToggled && "transition-transform duration-300",
              !isOpen && "rotate-180"
            )}
          >
            <ChevronDown />
          </IconButton>
        </div>
      </div>
    </div>
  );
};

export default BottomKPIBar;
